import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { invoicesService } from '../../services/api';
import { fmtCurrency, fmtDate, fmtDateTime } from '../../utils/formatters';
import { TONE_BADGE } from '../../utils/statusColors';
import { papelNaCobranca } from '../../utils/filaDeCobranca';
import {
  agruparRetidas, agruparSugestoes, aprovavel, FaturaRetida, GRUPO_DE_SUGESTAO, GrupoMontado, mesmoClienteNoLote,
  obsoletaDeHoje, rotuloDoAtraso, rotuloDoMarco, SugestaoDeCobranca,
} from '../../utils/sugestoesDeCobranca';
import { EstadoDaLista, mensagemDoErroDeCarga, useEstadoDaCarga } from '../../design';
import { barraDoEstado, CascaDaLista } from '../shared/lista/PecasDaLista';
import CascaDeGaveta from '../shared/CascaDeGaveta';
import { useConfirm, usePedirTexto } from '../shared/ConfirmDialog';
import { useAviso } from '../shared/Aviso';

/**
 * Financeiro › Cobrança › Sugestões (fase 2, C1 + B2): a fila que o servidor monta às 07:40, por trilha e marco, com a
 * prévia do texto final, a aprovação de admin (uma ou em lote), o descarte com motivo e a faixa do que NÃO entrou.
 * Prévia aprovada pelo Heitor em 30/10/2026 (S1 J1 O1): https://claude.ai/artifact/3r9uuPsfcfDr9vZRb5bDfQ
 *
 * Os botões seguem os papéis do BACKEND: aprovar e o lote são ADMIN; prévia e descarte são ADMIN e EDITOR; vendedor e
 * leitor só leem. Recusa do servidor vem `200` com `sent: false`, e a tela mostra a frase dele, nunca a engole.
 */

const fantasma = 'px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap border border-central-borda bg-central-cartao text-central-texto hover:bg-central-elevado disabled:opacity-40';
const DOT: Record<string, string> = { danger: 'bg-red-500', warning: 'bg-amber-500', info: 'bg-blue-500' };
const TOM_DA_LINHA: Record<string, 'danger' | 'warning' | null> = { danger: 'danger', warning: 'warning', info: null };

/** Como a linha nomeia cada código de `avisos`. Código desconhecido aparece com o próprio nome. */
const ROTULO_DO_AVISO: Record<string, string> = {
  SEM_NOTA_AUTORIZADA: 'sem nota autorizada',
  SEM_EMPENHO: 'sem empenho',
  SEM_DESTINATARIO: 'sem destinatário',
};

interface Resultado { id: number; sent: boolean; error?: string; oficioId?: number | null; oficioNumero?: string | null; recipients?: string[] }
interface Rodada { quando: string; itens: { resultado: Resultado; sugestao: SugestaoDeCobranca | undefined }[] }

export default function SugestoesDeCobranca({ onContagem, onAbrirOficios }: {
  /** Quantas aprováveis há, para o rótulo da sub-aba ("Sugestões · N"). */
  onContagem?: (n: number | null) => void;
  onAbrirOficios?: () => void;
}) {
  const papel = useMemo(papelNaCobranca, []);
  const avisar = useAviso();
  const confirmar = useConfirm();
  const pedirTexto = usePedirTexto();
  const carga = useEstadoDaCarga();
  const [lista, setLista] = useState<SugestaoDeCobranca[]>([]);
  const [retidas, setRetidas] = useState<FaturaRetida[] | null>(null);
  const [montadaEm, setMontadaEm] = useState<string | null>(null);
  const [erroDasRetidas, setErroDasRetidas] = useState<string | null>(null);
  const [selecao, setSelecao] = useState<Set<number>>(new Set());
  const [loteAvisado, setLoteAvisado] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const [rodada, setRodada] = useState<Rodada | null>(null);
  const [previaDe, setPreviaDe] = useState<SugestaoDeCobranca | null>(null);

  const carregar = useCallback(() => carga.rodar(async () => {
    const [sugeridas, falharam, obsoletas] = await Promise.all(
      ['sugerida', 'falhou', 'obsoleta'].map((st) => invoicesService.dunningSuggestions(st)),
    );
    const itens = (r: any): SugestaoDeCobranca[] => (Array.isArray(r?.items) ? r.items : []);
    setLista([...itens(sugeridas), ...itens(falharam), ...itens(obsoletas).filter((s) => obsoletaDeHoje(s))]);
    setSelecao(new Set());
    setLoteAvisado(false);
    // A faixa tem a carga dela: falhar aqui não pode esconder a fila, nem a fila vazia pode esconder a faixa.
    invoicesService.dunningSuggestionsHeld()
      .then((r: any) => { setRetidas(Array.isArray(r?.itens) ? r.itens : []); setMontadaEm(r?.montadaEm ?? null); setErroDasRetidas(null); })
      .catch((e: any) => { setRetidas(null); setErroDasRetidas(mensagemDoErroDeCarga(e)); });
  }), [carga.rodar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { carregar(); }, [carregar]);

  const vivas = useMemo(() => lista.filter(aprovavel), [lista]);
  useEffect(() => { onContagem?.(carga.atualizadoEm ? vivas.length : null); }, [vivas.length, carga.atualizadoEm]); // eslint-disable-line react-hooks/exhaustive-deps

  const grupos = useMemo(() => agruparSugestoes(lista), [lista]);
  const total = useMemo(() => vivas.reduce((t, s) => t + (Number(s.amountDue) || 0), 0), [vivas]);
  /** Clientes com mais de uma sugestão de e-mail viva na fila: o selo "N do cliente" da linha. */
  const doMesmoCliente = useMemo(() => {
    const n = new Map<number, number>();
    vivas.filter((s) => s.kind === 'email').forEach((s) => n.set(s.clientId, (n.get(s.clientId) || 0) + 1));
    return n;
  }, [vivas]);

  const selecionadas = useMemo(() => vivas.filter((s) => selecao.has(s.id)), [vivas, selecao]);
  const lote = useMemo(() => mesmoClienteNoLote(selecionadas), [selecionadas]);
  const alternar = (id: number) => {
    setLoteAvisado(false);
    setSelecao((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const registrarRodada = (resultados: Resultado[]) => {
    const porId = new Map(lista.map((s) => [s.id, s]));
    setRodada({ quando: new Date().toISOString(), itens: resultados.map((r) => ({ resultado: r, sugestao: porId.get(r.id) })) });
  };

  const aprovarIds = async (ids: number[]) => {
    if (!ids.length || aprovando) return;
    setAprovando(true);
    try {
      if (ids.length === 1) {
        const r = await invoicesService.approveDunningSuggestion(ids[0]);
        registrarRodada([{ id: ids[0], ...r }]);
      } else {
        const r = await invoicesService.approveDunningSuggestions(ids);
        registrarRodada(Array.isArray(r?.resultados) ? r.resultados : []);
      }
    } catch (e) {
      // Erro de rede ou 4xx/5xx: o servidor pode ter enviado parte do lote. A fila recarregada diz o que saiu.
      avisar(`A aprovação não respondeu: ${mensagemDoErroDeCarga(e)}. Confira a fila antes de aprovar de novo.`, 'error');
    } finally {
      setAprovando(false);
      setPreviaDe(null);
      carregar();
    }
  };

  const aprovarUma = async (s: SugestaoDeCobranca) => {
    const ok = await confirmar(s.kind === 'oficio' ? {
      title: `Criar o rascunho do ofício para ${s.clientName}?`,
      message: `O ofício nasce em Rascunho no módulo de Ofícios e ganha o número oficial agora. Nada é enviado: lá você revisa, emite e manda assinar.`,
      confirmLabel: 'Criar rascunho',
    } : {
      title: `Enviar a cobrança de ${s.invoices[0]?.code || 'fatura'} para ${s.clientName}?`,
      message: `${s.recipients.length === 1 ? 'Sai 1 e-mail' : `Sai para ${s.recipients.length} e-mails`} agora, e fica registrado em Contatos. O servidor confere de novo se a fatura ainda pode ser cobrada.`,
      confirmLabel: 'Enviar',
      irreversivel: true,
    });
    if (ok) aprovarIds([s.id]);
  };

  const aprovarLote = async (ids: number[]) => {
    const ok = await confirmar({
      title: `Aprovar ${ids.length} ${ids.length === 1 ? 'sugestão' : 'sugestões'}?`,
      message: 'Cada uma é conferida e enviada sozinha: se uma for recusada, as outras seguem. Os ofícios nascem em Rascunho.',
      confirmLabel: `Aprovar ${ids.length}`,
      irreversivel: true,
    });
    if (ok) aprovarIds(ids);
  };

  const pedirLote = () => {
    if (lote.repetidos.length && !loteAvisado) { setLoteAvisado(true); return; }
    aprovarLote(selecionadas.map((s) => s.id));
  };

  const descartar = async (s: SugestaoDeCobranca) => {
    const motivo = await pedirTexto({
      title: `Descartar a sugestão de ${s.clientName}?`,
      message: 'A sugestão sai da fila e não volta neste marco. O motivo fica registrado.',
      label: 'Motivo',
      placeholder: 'Ex.: cliente já pagou por TED, em conferência',
      obrigatorio: true,
      confirmLabel: 'Descartar',
    });
    if (!motivo || !motivo.trim()) return;
    try {
      await invoicesService.discardDunningSuggestion(s.id, motivo.trim());
      avisar('Sugestão descartada.', 'success');
      setPreviaDe(null);
      carregar();
    } catch (e) {
      avisar(`Não descartou: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-end justify-between gap-3 flex-wrap'>
        <div>
          <p className='text-sm text-central-texto-fraco'>Para aprovar</p>
          <p className='text-2xl font-bold text-central-texto tabular-nums' data-total-sugestoes>
            {carga.atualizadoEm ? `${vivas.length} ${vivas.length === 1 ? 'sugestão' : 'sugestões'} · ${fmtCurrency(total)}` : '—'}
          </p>
          {carga.atualizadoEm && (
            <p className='text-xs text-central-texto-fraco mt-0.5'>
              {montadaEm ? `Montada em ${fmtDateTime(montadaEm)} · ` : ''}lida em {fmtDateTime(carga.atualizadoEm)} · nada sai sem aprovação
            </p>
          )}
        </div>
        <button type='button' className={fantasma} onClick={carregar}>Atualizar</button>
      </div>

      {rodada && <ResultadoDaRodada rodada={rodada} onFechar={() => setRodada(null)} onAbrirOficios={onAbrirOficios} />}

      <EstadoDaLista
        carga={carga}
        temDados={carga.atualizadoEm !== null}
        oQue='as sugestões de cobrança'
        onTentarDeNovo={carregar}
        vazio={<p className='text-central-texto-fraco py-10 text-center'>Nenhuma sugestão.</p>}
      >
        <div className='space-y-3'>
          {grupos.map((g, i) => (
            <React.Fragment key={g.grupo}>
              {(i === 0 || GRUPO_DE_SUGESTAO[grupos[i - 1].grupo].trilha !== GRUPO_DE_SUGESTAO[g.grupo].trilha) && (
                <h3 className='pt-2 text-xs font-semibold uppercase tracking-wide text-central-texto-fraco'>{GRUPO_DE_SUGESTAO[g.grupo].trilha}</h3>
              )}
              <Grupo
                grupo={g}
                podeAprovar={papel.admin}
                podeLer={papel.registra}
                selecao={selecao}
                doMesmoCliente={doMesmoCliente}
                onSelecionar={alternar}
                onPrevia={setPreviaDe}
                onAprovar={aprovarUma}
                onDescartar={descartar}
              />
            </React.Fragment>
          ))}
        </div>
      </EstadoDaLista>

      {papel.admin && selecionadas.length > 0 && (
        <div className='sticky bottom-3 rounded-xl border border-blue-500/60 bg-central-cartao px-4 py-3 space-y-2' data-barra-do-lote>
          <div className='flex items-center gap-3 flex-wrap'>
            <span className='text-sm text-central-texto'><b>{selecionadas.length} {selecionadas.length === 1 ? 'selecionada' : 'selecionadas'}</b> · {fmtCurrency(selecionadas.reduce((t, s) => t + (Number(s.amountDue) || 0), 0))}</span>
            <span className='flex-1' />
            <button type='button' className={fantasma} onClick={() => { setSelecao(new Set()); setLoteAvisado(false); }}>Limpar</button>
            <button type='button' className={fantasma} disabled={aprovando} onClick={pedirLote}>{aprovando ? 'Aprovando…' : `Aprovar ${selecionadas.length}`}</button>
          </div>
          {loteAvisado && lote.repetidos.length > 0 && (
            <div role='alert' className='border-l-[3px] border-l-amber-500 bg-amber-500/10 rounded-md px-3 py-2 text-sm space-y-2' data-aviso-mesmo-cliente>
              {lote.repetidos.map((r) => (
                <p key={r.clientId} className='text-central-texto'>
                  <b>{r.sugestoes.length} faturas de {r.clientName} neste lote</b> ({r.sugestoes.map((s) => s.invoices[0]?.code).filter(Boolean).join(', ')}). Aprovadas juntas, saem {r.sugestoes.length} e-mails para o mesmo endereço hoje.
                  {' '}Fica a de maior marco, {r.fica.invoices[0]?.code} ({rotuloDoMarco(r.fica)}); as outras continuam na fila para amanhã.
                </p>
              ))}
              <div className='flex flex-wrap gap-2'>
                <button type='button' className={fantasma} disabled={aprovando || !lote.manter.length} onClick={() => aprovarLote(lote.manter)}>
                  Aprovar só {lote.manter.length === 1 ? 'a de maior marco' : `${lote.manter.length}, uma por cliente`}
                </button>
                <button type='button' className={fantasma} disabled={aprovando} onClick={() => aprovarLote(selecionadas.map((s) => s.id))}>Aprovar as {selecionadas.length} mesmo assim</button>
              </div>
            </div>
          )}
        </div>
      )}

      <FaixaDasRetidas retidas={retidas} erro={erroDasRetidas} />

      {previaDe && (
        <PreviaDaSugestao
          sugestao={previaDe}
          podeAprovar={papel.admin && aprovavel(previaDe)}
          podeDescartar={papel.registra}
          aprovando={aprovando}
          onClose={() => setPreviaDe(null)}
          onAprovar={() => aprovarUma(previaDe)}
          onDescartar={() => descartar(previaDe)}
        />
      )}
    </div>
  );
}

function Grupo({ grupo, podeAprovar, podeLer, selecao, doMesmoCliente, onSelecionar, onPrevia, onAprovar, onDescartar }: {
  grupo: GrupoMontado;
  podeAprovar: boolean;
  podeLer: boolean;
  selecao: Set<number>;
  doMesmoCliente: Map<number, number>;
  onSelecionar: (id: number) => void;
  onPrevia: (s: SugestaoDeCobranca) => void;
  onAprovar: (s: SugestaoDeCobranca) => void;
  onDescartar: (s: SugestaoDeCobranca) => void;
}) {
  const info = GRUPO_DE_SUGESTAO[grupo.grupo];
  return (
    <CascaDaLista>
      <section aria-label={info.titulo} data-grupo-sugestao={grupo.grupo}>
        <header className='px-4 py-3 border-b border-central-borda flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
          <span className={`w-2 h-2 rounded-full self-center ${DOT[info.tom]}`} aria-hidden />
          <h4 className='text-sm font-semibold text-central-texto'>{info.titulo}</h4>
          <span className='text-sm text-central-texto-fraco tabular-nums' data-contagem>
            {grupo.aprovaveis}{grupo.aprovaveis ? ` · ${fmtCurrency(grupo.total)}` : ''}
          </span>
          <p className='basis-full text-xs text-central-texto-fraco'>{info.sub}</p>
        </header>
        {grupo.sugestoes.length === 0 ? (
          <p className='m-3 px-3 py-2 rounded-lg border border-dashed border-central-borda text-sm text-central-texto-fraco'>{info.vazio}</p>
        ) : (
          <ul>
            {grupo.sugestoes.map((s) => {
              const viva = aprovavel(s);
              const obsoleta = s.status === 'obsoleta';
              const repetido = s.kind === 'email' && viva ? doMesmoCliente.get(s.clientId) || 0 : 0;
              const codigo = s.kind === 'oficio'
                ? `${s.invoices.length} ${s.invoices.length === 1 ? 'fatura' : 'faturas'}${s.contractNumber ? ` · contrato ${s.contractNumber}` : ''}`
                : s.invoices[0]?.code || '—';
              return (
                <li
                  key={s.id}
                  data-sugestao={s.id}
                  data-status={s.status}
                  className={`grid gap-x-4 gap-y-1 items-center px-4 py-3 border-b last:border-b-0 border-central-borda md:grid-cols-[1.25rem_minmax(0,1.4fr)_6.5rem_7rem_minmax(0,1.6fr)_auto] ${barraDoEstado(viva ? TOM_DA_LINHA[info.tom] : null)} ${obsoleta ? 'opacity-60' : ''} ${selecao.has(s.id) ? 'bg-blue-500/5' : ''}`}
                >
                  <div>
                    {podeAprovar && viva && (
                      <input type='checkbox' aria-label={`Selecionar ${s.clientName}`} checked={selecao.has(s.id)} onChange={() => onSelecionar(s.id)} />
                    )}
                  </div>
                  <div className='min-w-0'>
                    <div className='font-medium text-central-texto text-sm truncate' title={s.clientName}>{s.clientName}</div>
                    <div className='text-xs text-central-texto-fraco font-mono truncate'>{codigo}</div>
                  </div>
                  <div className='text-sm text-central-texto-fraco tabular-nums'>
                    <div>{rotuloDoMarco(s)}</div>
                    <div className='text-xs'>{rotuloDoAtraso(s.daysOverdue)}</div>
                  </div>
                  <div className={`text-sm tabular-nums md:text-right ${obsoleta ? 'line-through text-central-texto-fraco' : 'text-central-texto'}`}>{fmtCurrency(s.amountDue)}</div>
                  <div className='text-sm min-w-0 flex flex-wrap gap-1 items-center'>
                    {obsoleta && (
                      <>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${TONE_BADGE.warning}`}>obsoleta</span>
                        <span className='text-xs text-central-texto-fraco' data-motivo-obsoleta>{s.blockedReason || 'A fatura mudou desde a montagem.'}</span>
                      </>
                    )}
                    {s.status === 'falhou' && (
                      <>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${TONE_BADGE.danger}`}>falhou</span>
                        <span className='text-xs text-central-texto-fraco' data-erro-do-envio>{s.sendError || 'O envio não foi aceito.'} Pode aprovar de novo.</span>
                      </>
                    )}
                    {s.avisoPreVencimento && viva && <span className={`px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.progress}`}>aviso, não cobrança</span>}
                    {(s.avisos || []).map((a) => (
                      <span key={a} data-aviso={a} className={`px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.warning}`}>{ROTULO_DO_AVISO[a] || a}</span>
                    ))}
                    {repetido > 1 && <span data-mesmo-cliente className={`px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.danger}`}>{repetido} do cliente</span>}
                  </div>
                  <div className='flex flex-wrap gap-1.5 md:justify-end'>
                    {podeLer && !obsoleta && <button type='button' className={fantasma} onClick={() => onPrevia(s)}>Prévia</button>}
                    {podeAprovar && viva && (
                      <button type='button' className={fantasma} onClick={() => onAprovar(s)}>{s.kind === 'oficio' ? 'Criar rascunho' : 'Aprovar'}</button>
                    )}
                    {podeLer && <button type='button' className={fantasma} onClick={() => onDescartar(s)}>Descartar</button>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </CascaDaLista>
  );
}

/** O resultado da última aprovação, por sugestão. Fica até fechar, mesmo depois de a fila recarregar. */
function ResultadoDaRodada({ rodada, onFechar, onAbrirOficios }: { rodada: Rodada; onFechar: () => void; onAbrirOficios?: () => void }) {
  const saiu = rodada.itens.filter((i) => i.resultado.sent).length;
  const recusadas = rodada.itens.length - saiu;
  return (
    <section
      aria-label='Resultado da aprovação'
      data-resultado-do-lote
      className={`rounded-xl border border-central-borda bg-central-cartao px-4 py-3 ${barraDoEstado(recusadas ? 'danger' : 'success')}`}
    >
      <div className='flex items-baseline justify-between gap-3 flex-wrap'>
        <p className='text-sm text-central-texto' role={recusadas ? 'alert' : undefined}>
          <b>
            {rodada.itens.length === 1
              ? (saiu ? 'Aprovada.' : 'Recusada pelo servidor.')
              : `${saiu} de ${rodada.itens.length} ${saiu === 1 ? 'saiu' : 'saíram'}.${recusadas ? ` ${recusadas} ${recusadas === 1 ? 'foi recusada' : 'foram recusadas'}.` : ''}`}
          </b>
          {rodada.itens.length > 1 && recusadas > 0 && <span className='text-central-texto-fraco'> Nada foi desfeito: cada sugestão é enviada sozinha.</span>}
          <span className='text-xs text-central-texto-fraco'> · {fmtDateTime(rodada.quando)}</span>
        </p>
        <button type='button' className={fantasma} onClick={onFechar}>Fechar</button>
      </div>
      <ul className='mt-2'>
        {rodada.itens.map(({ resultado: r, sugestao: s }) => (
          <li key={r.id} data-resultado={r.id} className='grid gap-x-3 py-1.5 border-t border-central-borda first:border-t-0 md:grid-cols-[minmax(0,1.3fr)_auto_minmax(0,2fr)] items-baseline text-sm'>
            <div className='min-w-0 truncate'>
              <span className='text-central-texto'>{s?.clientName || `Sugestão #${r.id}`}</span>{' '}
              <span className='text-xs text-central-texto-fraco font-mono'>{s?.kind === 'oficio' ? 'ofício' : s?.invoices[0]?.code}</span>
            </div>
            <span className={`justify-self-start px-2 py-0.5 rounded-full text-[11px] font-semibold ${r.sent ? TONE_BADGE.success : TONE_BADGE.danger}`}>
              {r.sent ? (s?.kind === 'oficio' ? 'rascunho criado' : 'enviada') : 'recusada'}
            </span>
            <div className={r.sent ? 'text-central-texto-fraco' : 'text-central-texto'} data-motivo-da-recusa={r.sent ? undefined : true}>
              {!r.sent
                ? (r.error || 'O servidor recusou sem dizer o motivo.')
                : s?.kind === 'oficio'
                  ? (
                    <>
                      {r.oficioNumero ? `Rascunho ${r.oficioNumero} criado em Ofícios.` : 'Rascunho criado em Ofícios.'}
                      {onAbrirOficios && <button type='button' className='ml-2 underline hover:text-central-texto' onClick={onAbrirOficios}>Abrir em Ofícios</button>}
                    </>
                  )
                  : `para ${(r.recipients || []).length || '—'} ${(r.recipients || []).length === 1 ? 'e-mail' : 'e-mails'} · registrada em Contatos`}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** O que a montagem NÃO sugeriu, por motivo, com as frases do servidor. Erro de leitura é erro, nunca "nada retido". */
function FaixaDasRetidas({ retidas, erro }: { retidas: FaturaRetida[] | null; erro: string | null }) {
  const motivos = useMemo(() => agruparRetidas(retidas || []), [retidas]);
  const total = (retidas || []).reduce((t, f) => t + (Number(f.amountDue) || 0), 0);
  return (
    <CascaDaLista>
      <section aria-label='Não entraram hoje' data-faixa-retidas className='px-4 py-3'>
        <div className='flex items-baseline justify-between gap-3 flex-wrap'>
          <h3 className='text-sm font-semibold text-central-texto'>Não entraram hoje, e por quê</h3>
          {retidas && <span className='text-sm text-central-texto-fraco tabular-nums'>{retidas.length} {retidas.length === 1 ? 'fatura' : 'faturas'}{retidas.length ? ` · ${fmtCurrency(total)}` : ''}</span>}
        </div>
        {erro ? (
          <p role='alert' className={`mt-2 px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`}>Não consegui ler o que ficou de fora: {erro}</p>
        ) : !retidas ? (
          <p className='mt-2 text-sm text-central-texto-fraco'>Lendo…</p>
        ) : motivos.length === 0 ? (
          <p className='mt-2 text-sm text-central-texto-fraco'>Nenhuma fatura em aberto ficou de fora.</p>
        ) : (
          <ul className='mt-1'>
            {motivos.map((m) => (
              <li key={m.codigo} data-retencao={m.codigo} className='grid gap-x-4 gap-y-0.5 py-2 border-t border-central-borda first:border-t-0 md:grid-cols-[minmax(0,1.2fr)_7rem_minmax(0,2fr)] items-baseline'>
                <div className='min-w-0'>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.neutral}`}>{m.rotulo}</span>
                  <div className='text-xs text-central-texto-fraco mt-0.5 truncate' title={m.clientes.join(', ')}>
                    {m.faturas.length} · {m.clientes.slice(0, 3).join(', ')}{m.clientes.length > 3 ? ` e mais ${m.clientes.length - 3}` : ''}
                  </div>
                </div>
                <div className='text-sm text-central-texto tabular-nums md:text-right'>{fmtCurrency(m.total)}</div>
                <div className='text-xs text-central-texto-fraco'>{m.motivo}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </CascaDaLista>
  );
}

/** A prévia: destinatários e o texto FINAL que o servidor montou pelo mesmo caminho do envio. */
function PreviaDaSugestao({ sugestao: s, podeAprovar, podeDescartar, aprovando, onClose, onAprovar, onDescartar }: {
  sugestao: SugestaoDeCobranca;
  podeAprovar: boolean;
  podeDescartar: boolean;
  aprovando: boolean;
  onClose: () => void;
  onAprovar: () => void;
  onDescartar: () => void;
}) {
  const [dados, setDados] = useState<any | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const daTela = useRef(s.id);

  useEffect(() => {
    daTela.current = s.id;
    setDados(null);
    setErro(null);
    invoicesService.dunningSuggestionPreview(s.id)
      .then((r: any) => { if (daTela.current === s.id) setDados(r); })
      .catch((e: any) => { if (daTela.current === s.id) setErro(mensagemDoErroDeCarga(e)); });
  }, [s.id]);

  const oficio = s.kind === 'oficio';
  const destinatarios: string[] = dados?.recipients || s.recipients || [];
  const podeSeguir = podeAprovar && !!dados && !dados.obsoleto;

  return (
    <CascaDeGaveta
      titulo={s.clientName}
      subtitulo={`${oficio ? `Ofício · ${s.invoices.length} ${s.invoices.length === 1 ? 'fatura' : 'faturas'}${s.contractNumber ? ` · contrato ${s.contractNumber}` : ''}` : s.invoices[0]?.code} · ${rotuloDoMarco(s)}`}
      onClose={onClose}
      largura='painel'
      rotulo={`Prévia da sugestão de ${s.clientName}`}
      rodape={(podeAprovar || podeDescartar) ? (
        <div className='flex justify-end gap-2'>
          {podeDescartar && <button type='button' className={fantasma} onClick={onDescartar}>Descartar…</button>}
          {podeAprovar && (
            <button type='button' className={fantasma} disabled={!podeSeguir || aprovando} onClick={onAprovar}>
              {aprovando ? 'Aprovando…' : oficio ? 'Criar rascunho do ofício' : 'Aprovar e enviar'}
            </button>
          )}
        </div>
      ) : undefined}
    >
      <div className='space-y-3 text-sm'>
        {erro ? (
          <p role='alert' className={`px-3 py-2 rounded-lg ${TONE_BADGE.danger}`}>Não consegui montar a prévia: {erro}</p>
        ) : !dados ? (
          <p className='text-central-texto-fraco'>Montando a prévia no servidor…</p>
        ) : (
          <>
            {dados.obsoleto && (
              <p role='alert' className='border-l-[3px] border-l-amber-500 bg-amber-500/10 rounded-md px-3 py-2 text-central-texto' data-previa-obsoleta>
                <b>Esta sugestão não pode mais ser aprovada.</b> {dados.blockedReason || 'A fatura mudou desde a montagem.'}
              </p>
            )}
            {oficio && (
              <p className='border-l-[3px] border-l-amber-500 bg-amber-500/10 rounded-md px-3 py-2 text-central-texto' data-aviso-rascunho>
                <b>Aprovar não envia nada.</b> Cria um ofício em <b>Rascunho</b> no módulo de Ofícios, e só então ele ganha o número oficial. Lá você revisa, emite e manda assinar.
              </p>
            )}
            <dl className='grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1'>
              <dt className='text-central-texto-fraco'>{oficio ? 'Destinatário' : 'Para'}</dt>
              <dd className='text-central-texto break-all' data-destinatarios>
                {destinatarios.length ? destinatarios.join(', ') : <span className='text-amber-300'>Nenhum destinatário. Cadastre o e-mail antes de aprovar.</span>}
              </dd>
              <dt className='text-central-texto-fraco'>Assunto</dt>
              <dd className='text-central-texto'>{dados.subject || '—'}</dd>
              <dt className='text-central-texto-fraco'>Valor</dt>
              <dd className='text-central-texto tabular-nums'>{fmtCurrency(dados.amountDue ?? s.amountDue)}{oficio ? '' : ' (calculado agora, com encargos)'}</dd>
            </dl>
            {oficio && s.invoices.length > 0 && (
              <ul className='text-xs text-central-texto-fraco'>
                {s.invoices.map((f) => <li key={f.id} className='font-mono'>{f.code} · vencida em {fmtDate(f.dueDate)} · {fmtCurrency(f.amountDue)}</li>)}
              </ul>
            )}
            {oficio ? (
              <div className='rounded-lg bg-white text-slate-900 px-4 py-3 whitespace-pre-wrap font-serif leading-relaxed' data-corpo-do-oficio>{dados.html}</div>
            ) : (
              <iframe title='Texto final do e-mail' sandbox='' srcDoc={dados.html || ''} className='w-full h-[480px] rounded-lg border border-central-borda bg-white' data-corpo-do-email />
            )}
          </>
        )}
      </div>
    </CascaDeGaveta>
  );
}
